import React from "react";

/**
 * TranscriptPreview
 * Dark Nexora theme. Transcript renders in an editable textarea —
 * user can tap directly to correct, keyboard opens naturally on mobile.
 * Additional speech via Continue Listening appends to current value.
 * All existing button behavior and webhook flow preserved.
 */
export function TranscriptPreview({
  transcript,           // current transcript string (controlled from parent)
  onTranscriptChange,   // (newValue: string) => void — parent keeps source of truth
  isLoading,
  onConfirm,            // Send / submit answer
  onRetry,              // Clear transcript, back to idle
  onCancel,             // Abandon without submitting
  onContinue,           // Resume mic — speech appends to existing transcript
  onBack,               // Back — returns to idle (workflow-style)
}) {
  const isEmpty = !transcript || transcript.trim().length === 0;

  return (
    <div className="transcript-area nx-transcript-area">

      <div className="transcript-label nx-transcript-label">Your answer</div>

      {/* Editable textarea — replaces read-only box.
          Tapping opens mobile keyboard directly. No edit button needed. */}
      <textarea
        className={`nx-transcript-textarea ${isEmpty ? "nx-transcript-textarea-empty" : ""}`}
        value={transcript}
        onChange={(e) => onTranscriptChange && onTranscriptChange(e.target.value)}
        placeholder="No speech detected — tap here to type, or speak above."
        rows={4}
        disabled={isLoading}
        aria-label="Your answer — tap to edit"
      />

      <div className="transcript-actions nx-transcript-actions">

        {/* PRIMARY: Send */}
        <button
          className="nx-btn nx-btn-gold"
          onClick={onConfirm}
          disabled={isEmpty || isLoading}
        >
          {isLoading ? (
            <span className="btn-loading">
              <span className="nx-spinner nx-spinner-sm" />
              Saving…
            </span>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="btn-icon">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Send
            </>
          )}
        </button>

        {/* SECONDARY: Continue Listening */}
        {onContinue && (
          <button
            className="nx-btn nx-btn-outline"
            onClick={onContinue}
            disabled={isLoading}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="btn-icon">
              <rect x="9" y="2" width="6" height="12" rx="3"/>
              <path d="M5 10a7 7 0 0 0 14 0"/>
              <line x1="12" y1="19" x2="12" y2="22"/>
              <line x1="9" y1="22" x2="15" y2="22"/>
            </svg>
            Continue Listening
          </button>
        )}

        {/* TERTIARY row: Retry + Back — equal workflow weight */}
        <div className="nx-transcript-row">
          <button
            className="nx-btn nx-btn-ghost nx-btn-half"
            onClick={onRetry}
            disabled={isLoading}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="btn-icon">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 .49-3.51" />
            </svg>
            Retry
          </button>

          {onBack && (
            <button
              className="nx-btn nx-btn-ghost nx-btn-half"
              onClick={onBack}
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="btn-icon">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
              Back
            </button>
          )}
        </div>

        {/* GHOST: Cancel */}
        <button
          className="nx-transcript-cancel"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>

      </div>
    </div>
  );
}
